export function toSnakeCase<T extends Record<string, any>>(obj: T): Record<string, any> {
  if (!obj || typeof obj !== 'object') return obj;

  if (Array.isArray(obj)) {
    return obj.map(item => toSnakeCase(item));
  }

  const result: Record<string, any> = {};

  for (const [key, value] of Object.entries(obj)) {
    const snakeCaseKey = camelToSnake(key);

    if (value === null || value === undefined) {
      result[snakeCaseKey] = value;
    } else if (Array.isArray(value)) {
      result[snakeCaseKey] = value.map(item =>
        typeof item === 'object' && item !== null ? toSnakeCase(item) : item
      );
    } else if (value instanceof Date) {
      result[snakeCaseKey] = value.toISOString();
    } else if (typeof value === 'object') {
      result[snakeCaseKey] = toSnakeCase(value);
    } else {
      result[snakeCaseKey] = value;
    }
  }

  return result;
}

export function toCamelCase<T>(obj: Record<string, any>): T {
  if (!obj || typeof obj !== 'object') return obj as T;

  if (Array.isArray(obj)) {
    return obj.map(item => toCamelCase(item)) as T;
  }

  const result: Record<string, any> = {};

  for (const [key, value] of Object.entries(obj)) {
    const camelCaseKey = snakeToCamel(key);

    if (value === null || value === undefined) {
      result[camelCaseKey] = value;
    } else if (Array.isArray(value)) {
      result[camelCaseKey] = value.map(item =>
        typeof item === 'object' && item !== null ? toCamelCase(item) : item
      );
    } else if (typeof value === 'string' && isIsoDateString(value)) {
      result[camelCaseKey] = new Date(value);
    } else if (typeof value === 'object') {
      result[camelCaseKey] = toCamelCase(value);
    } else {
      result[camelCaseKey] = value;
    }
  }

  return result as T;
}

function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

function isIsoDateString(value: string): boolean {
  if (typeof value !== 'string') return false;
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?$/.test(value);
}

export function formatApiRequest<T extends Record<string, any>>(data: T): Record<string, any> {
  if (!data) return data;

  const formatted: Record<string, any> = {};

  for (const [key, value] of Object.entries(data)) {
    if (value instanceof Date) {
      formatted[key] = value.toISOString();
    } else if (Array.isArray(value)) {
      formatted[key] = value.map(item => {
        if (item instanceof Date) {
          return item.toISOString();
        } else if (typeof item === 'object' && item !== null) {
          return formatApiRequest(item);
        }
        return item;
      });
    } else if (typeof value === 'object' && value !== null) {
      formatted[key] = formatApiRequest(value);
    } else {
      formatted[key] = value;
    }
  }

  return toSnakeCase(formatted);
}

export function toUrlParams(obj: Record<string, any>): URLSearchParams {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(obj)) {
    if (value !== null && value !== undefined) {
      if (Array.isArray(value)) {
        value.forEach(item => {
          params.append(key, String(item));
        });
      } else if (value instanceof Date) {
        params.append(key, value.toISOString());
      } else if (typeof value === 'object') {
        params.append(key, JSON.stringify(value));
      } else {
        params.append(key, String(value));
      }
    }
  }

  return params;
}

export function debugConversion<T>(obj: T, label = 'Object'): T {
  if (process.env.NODE_ENV === 'development') {
    console.group(`🔄 ${label} Conversion`);
    console.log('Original:', obj);
    if (typeof obj === 'object' && obj !== null) {
      console.log('Formatted for API:', formatApiRequest(obj as any));
    }
    console.groupEnd();
  }
  return obj;
}
